import React, {useEffect, useState} from 'react';
import './News.css';
import {getNewsFiltered} from "../../api/newsService";
import CopyrightText from "../components/CopyrightText/CopyrightText";
import {useDisplayMode} from "../../hooks/useDisplayMode";
import NewsCard from "./components/NewsCard/NewsCard";
import NewsCardSceleton from "./components/NewsCardSceleton/NewsCardSceleton";

// Fake data
export type NewsItem = {
  title: string;
  description: string;
  img: string;
  link: string;
  expanded: boolean;
};

const News: React.FC = () => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const {isDesktop} = useDisplayMode()

  async function getItems () {
    try {
      setLoading(true);
      const response =  await getNewsFiltered({sort: "desc", pagination: {page: 1, pageSize: 10}});
      setItems(response.map(item => {
        return {
          title: item.title,
          description: item.description,
          link: item.link,
          img: item.featuredImage.url,
          expanded: false,
        }
      }) || [])
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getItems().finally();
  }, []);

  const expand = (index: number) => {
    setItems(prev => prev.map((it, i) => i === index
      ? { ...it, expanded: true } : { ...it, expanded: false }));
  };

  const collapse = (index: number) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, expanded: false } : it));
  };

  const onLinkClicked = (index: number) => {
    const url = items[index]?.link;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="news-page">
      <h2 className="news-title">Last news</h2>
      <div className="news-columns">
        {loading ? (
          isDesktop ? (
            (() => {
              const count = 8;
              const left = Array.from({ length: count }).filter((_, i) => i % 2 === 0);
              const right = Array.from({ length: count }).filter((_, i) => i % 2 === 1);
              return (
                <>
                  <div className="news-column">
                    {left.map((_, idx) => (
                      <NewsCardSceleton key={`skl-l-${idx}`} />
                    ))}
                  </div>
                  <div className="news-column">
                    {right.map((_, idx) => (
                      <NewsCardSceleton key={`skl-r-${idx}`} />
                    ))}
                  </div>
                </>
              );
            })()
          ) : (
            <div className="news-column">
              {Array.from({ length: 3 }).map((_, idx) => (
                <NewsCardSceleton key={`skl-m-${idx}`} />
              ))}
            </div>
          )
        ) : (
          isDesktop ? (
            (() => {
              const leftItems = items.filter((_, i) => i % 2 === 0);
              const rightItems = items.filter((_, i) => i % 2 === 1);
              const renderCard = (item: NewsItem, idx: number, originalIndex: number) => (
                <NewsCard
                  key={originalIndex}
                  item={item}
                  index={originalIndex}
                  onExpand={expand}
                  onCollapse={collapse}
                  onLinkClicked={onLinkClicked}
                />
              );
              return (
                <>
                  <div className="news-column">
                    {leftItems.map((item, i) => renderCard(item, i, i * 2))}
                  </div>
                  <div className="news-column">
                    {rightItems.map((item, i) => renderCard(item, i, i * 2 + 1))}
                  </div>
                </>
              );
            })()
          ) : (
            <div className="news-column">
              {items.map((item, idx) => (
                <NewsCard
                  key={idx}
                  item={item}
                  index={idx}
                  onExpand={expand}
                  onCollapse={collapse}
                  onLinkClicked={onLinkClicked}
                />
              ))}
            </div>
          )
        )}
      </div>
      <CopyrightText />
    </div>
  );
};

export default News;
